import type { BridgeOptionViewModel } from './types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { TitleListCell } from '@sunwebgroup/uds-ui/title-list-cell'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import { updateBridgeService } from '@/scopes/checkout/domain/actions/services/bridge'
import { createAncillariesClickedEvent } from '@/scopes/checkout/domain/analytics'
import { useService } from '@/scopes/checkout/domain/hooks'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import styles from './BridgeOptionRow.module.css'
import { BridgeOptionSheet } from './BridgeOptionSheet'

interface BridgeOptionRowProps {
  option: BridgeOptionViewModel
}

export function BridgeOptionRow({ option }: BridgeOptionRowProps) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isDesktopSm } = useBreakpoints()
  const { service, setServiceValue } = useService(ServiceType.Bridge)
  const [isEditing, setIsEditing] = React.useState(false)

  const openEditPanel = React.useCallback(() => {
    setIsEditing(true)
    sendEvent(
      createAncillariesClickedEvent({
        description: 'Bridge',
        element: 'element clicked',
        type: 'select Bridge',
        result: `open ${option.code}`,
      }),
    )
  }, [option.code, sendEvent])

  const handleSave = React.useCallback((participantIds: string[]) => {
    if (!service)
      return
    setServiceValue(updateBridgeService({ optionId: option.id, checkedParticipantIds: participantIds })(service))
    setIsEditing(false)
    sendEvent(
      createAncillariesClickedEvent({
        description: 'Bridge',
        element: 'button',
        type: 'select Bridge',
        result: `save ${option.code}`,
      }),
    )
  }, [setServiceValue, service, option.code, sendEvent])

  const handleCancel = React.useCallback(() => {
    setIsEditing(false)
  }, [])

  const assignedParticipants = option.availableParticipants.filter(p =>
    option.assignedParticipantIds.includes(p.id),
  )
  const totalPrice = assignedParticipants.reduce((sum, participant) => {
    const assignment = option.priceAssignments.find(a => a.participantIds.includes(participant.id))
    return sum + (assignment?.price ?? option.price)
  }, 0)
  const participantNames = assignedParticipants.map(p => p.name).join(', ')

  if (!service)
    return null

  return (
    <div className={styles.optionRow}>
      <ListItem hasHover onClick={openEditPanel} className={styles.optionHeader}>
        <TitleListCell
          size="L"
          overline={option.label}
          title={option.isActive ? intl.formatMessage({ id: 'checkout_steps_ancillaries_bridge_row_title_playing' }) : intl.formatMessage({ id: 'checkout_steps_ancillaries_bridge_row_title_make_selection' })}
          subtitle={option.isActive ? participantNames : intl.formatMessage({ id: 'checkout_steps_ancillaries_bridge_row_subtitle_no_participants' })}
        />
        <div className={styles.itemPrice}>
          <div className={styles.priceBlock}>
            {totalPrice > 0 && (
              <Typography variant="body-s-regular-snug" className={styles.price}>
                {intl.formatCurrency(totalPrice, { showSign: false })}
              </Typography>
            )}
            {!isDesktopSm && (
              <Button variant="transparentNeutral" aria-label={option.isActive ? intl.formatMessage({ id: 'checkout_steps_ancillaries_bridge_row_edit_button' }) : intl.formatMessage({ id: 'checkout_steps_ancillaries_bridge_row_select_button' })} size="S">
                <Button.Icon glyph="ChevronRight" size={24} />
              </Button>
            )}
            {isDesktopSm && (
              <Button variant="secondary" size="S">
                {option.isActive ? intl.formatMessage({ id: 'checkout_steps_ancillaries_bridge_row_edit_button' }) : intl.formatMessage({ id: 'checkout_steps_ancillaries_bridge_row_select_button' })}
              </Button>
            )}
          </div>
        </div>
      </ListItem>

      <BridgeOptionSheet
        option={option}
        isOpen={isEditing}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </div>
  )
}
